import { Icon, Intent, Position, Tooltip } from "@blueprintjs/core";
import axios from "axios";
import { DateToLocal } from "components/dates/DateToLocal";
import { Filter, FilterButton } from "components/filtering";
import { FormLocationSelector, FormTextInput, FormToggleList } from "components/form-fields";
import { ShowHide } from "components/layout/ShowHide";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { EmployeeStatus } from "components/status/EmployeeStatus";
import { UserHasAccess } from "components/user-access/UserAccess";
import { useDebounce } from "hooks/useDebounce/useDebounce";
import { useQuery } from "hooks/useQuery/useQuery";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EmployeeAssessmentsService, EmployeeService } from "services";

const upcomingToggleFilters = [
    {
        key: 'EMPLOYEESYETTOCOMPLETEINITIAL',
        label: 'Yet to complete initial assessment',
        colour: '#FF9076',
        icon: 'document'
    },
    {
        key: 'EMPLOYEESYETTOCOMPLETEFOLLOWUP',
        label: 'Yet to complete follow-up assessment',
        colour: '#FF9076',
        icon: 'document'
    },
    {
        key: 'EMPLOYEESWITHOVERDUEASSESSMENTS',
        label: 'With overdue assessments',
        colour: '#FF9076',
        icon: 'time'
    },
    {
        key: 'EMPLOYEESWITHOVERDUETRAINING',
        label: 'With overdue training',
        colour: '#FF9076',
        icon: 'time'
    }
];

export function EmployeeAssessmentsUpcoming() {
    const requiredAction = "EmployeeAssessments:View";
    const canAccessPage = UserHasAccess(requiredAction);
    const [isLoading, setIsLoading] = useState(true);
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const query = useQuery();
    const queryFilter = query.get("filter");
    const debounceTimeout = 750;
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const reduxLocations = useSelector(
        (state) => state.employeeAssessments.locations
    );
    const dispatch = useDispatch();
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);

    const tableHeaders = [
        "Employee Name",
        "Location",
        "Category",
        "Has Completed Initial Assessment",
        "Next Assessment Date",
        "Last Assessment Date",
        "Next Training Date",
        "Last Training Date"
    ];

    const sortableHeaders = [
        { headerName: "Employee Name", sortName: "employeeFullName" },
        { headerName: "Location", sortName: "LocationName" },
        { headerName: "Category", sortName: "employeeCategoryName" },
        { headerName: "Has Completed Initial Assessment", sortName: "hasCompletedInitialAssessment" },
        { headerName: "Next Assessment Date", sortName: "nextAssessmentDueDate" },
        { headerName: "Last Assessment Date", sortName: "lastAssessmentDate" },
        { headerName: "Next Training Date", sortName: "nextTrainingDueDate" },
        { headerName: "Last Training Date", sortName: "lastTrainingDate" }
    ];

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentSearchTerm = useRef();
    const currentFilterStatus = useRef();
    const locationFilterOpen = useRef(false);
    const inputRef = useRef(null);

    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [employeeAssessmentOptions, setEmployeeAssessmentOptions] = useState({
        trainingTypes: [],
        riskScores: [],
        statsFilters: []
    });

    const [statCounts, setStatCounts] = useState([]);

    const filterStatuses = employeeAssessmentOptions.statsFilters
        .filter(f => upcomingToggleFilters.some(o => o.key === f.filterKey))
        .map(stat => {
            const override = upcomingToggleFilters.find(o => o.key === stat.filterKey);
            const count = statCounts.find(s => s.id === stat.id)?.count ?? 0;

            return {
                key: stat.filterKey,
                name: override.label,
                count: count,
                icon: override.icon,
                colour: override.colour
            };
        });
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [selectedFilterStatus, setSelectedFilterStatus] = useState();

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_ASSESSMENTS_LOCATIONS",
            payload: locations,
        });
    }

    function onFilterStatusChange(key) {
        setSelectedFilterStatus(key);
    }

    function onLocationFilterOpened() {
        locationFilterOpen.current = true;
    }

    function onLocationFilterClosed() {
        locationFilterOpen.current = false;
        fetchTableData();
    }

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
    }, [reduxLocations]);

    useEffect(() => {
        if (locationFilterOpen.current) {
            return;
        }
        loadStats(reduxLocations);
    }, [reduxLocations]);

    useEffect(() => {
        if (!queryFilter || !employeeAssessmentOptions) {
            return;
        }

        const filter = employeeAssessmentOptions.statsFilters.find(
            (cf) => cf.filterKey === queryFilter.toUpperCase()
        );

        if (!filter) {
            return;
        }

        if (upcomingToggleFilters.some(o => o.key === filter.filterKey)) {
            setSelectedFilterStatus(filter.filterKey);
        }
    }, [queryFilter, employeeAssessmentOptions]);

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (f) {
            return {
                key: f.name,
                ...f,
            };
        });

        if (reduxLocations != null) {
            const mappedLocations = reduxLocations.map(function (l) {
                return { key: "Location", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        if (currentFilterStatus.current != null) {
            mappedFilters = [
                ...mappedFilters,
                {
                    key: currentFilterStatus.current,
                    value: currentFilterStatus.current,
                },
            ];
        }

        return mappedFilters;
    }

    function fetchTableData(forceRefresh) {
        if (!reduxLocations) {
            setTableRows([]);
            setTotalRecords(0);
            setIsLoading(false);
            return;
        }

        if (canAccessPage && initalSetupComplete) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                currentLocations.current === reduxLocations.length &&
                currentFilterStatus.current === selectedFilterStatus &&
                !forceRefresh
            ) {
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;
            currentLocations.current = reduxLocations.length;
            currentFilterStatus.current = selectedFilterStatus;

            setIsLoading(true);

            const mappedFilters = mapFilters();

            const pageSize = 50;

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mappedFilters,
                searchTerm: debouncedSearchTerm,
            };

            axios.all([loadData(searchRequest), loadStats(reduxLocations)]).then(
                () => {
                    setIsLoading(false);

                    setTimeout(function () {
                        if (inputRef.current != null) {
                            inputRef.current.focus();
                        }
                    });
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve employee assessments, please try again"
                    );
                }
            );
        }
    }

    const loadData = (searchRequest) => {
        return EmployeeAssessmentsService.getEmployeeOverviews(searchRequest).then(
            (r) => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((d) => {
                        return [
                            <div key={d.employeeId} className="inline-items">
                                <EmployeeStatus
                                    statusId={d.status.id}
                                    typeId={d.type.id}
                                    typeAttributeMappings={employeeTypeOptions}
                                    statusAttributeMappings={employeeStatusOptions}
                                />
                                <a href={`/employee/${d.employeeId}/assessments`}>
                                    {d.employeeFullName}
                                </a>
                            </div>,
                            d.locationName,
                            d.category.name,
                            d.hasCompletedInitialAssessment ? "Yes" : "No",
                            <div key={`next-assessment-${d.employeeId}`}>
                                <Tooltip content={d.hasOverdueAssessments ? "Employee has overdue assessment" : ""} position={Position.RIGHT}>
                                    <div className="inline-items nowrap">
                                        <span>
                                            <DateToLocal format="DD/MM/YYYY">
                                                {d.nextAssessmentDueDate}
                                            </DateToLocal>
                                        </span>
                                        <ShowHide
                                            evaluator={d.hasOverdueAssessments}
                                            show={
                                                <Icon icon="warning-sign" color="#F0578A" />
                                            }
                                        />
                                    </div>
                                </Tooltip>
                            </div>,
                            <DateToLocal key={`last-assessment-${d.employeeId}`} format="DD/MM/YYYY">
                                {d.lastAssessmentDate}
                            </DateToLocal>,
                            <div key={`next-training-${d.employeeId}`}>
                                <Tooltip content={d.hasOverdueTraining ? "Employee has overdue training" : ""} position={Position.RIGHT}>
                                    <div className="inline-items nowrap">
                                        <span>
                                            <DateToLocal format="DD/MM/YYYY">
                                                {d.nextTrainingDueDate}
                                            </DateToLocal>
                                        </span>
                                        <ShowHide
                                            evaluator={d.hasOverdueTraining}
                                            show={
                                                <Icon icon="warning-sign" color="#F0578A" />
                                            }
                                        />
                                    </div>
                                </Tooltip>
                            </div>,
                            <DateToLocal key={`last-training-${d.employeeId}`} format="DD/MM/YYYY">
                                {d.lastTrainingDate}
                            </DateToLocal>
                        ];
                    });
                }

                setTableRows(data);
                setTotalRecords(r.totalCount);
            }
        );
    };

    const loadStats = (locationIds) => {
        return EmployeeAssessmentsService.getStats(locationIds).then(
            (r) => {
                setStatCounts(r);
            }
        );
    };

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        initalSetupComplete,
        selectedFilterStatus,
    ]);

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                EmployeeAssessmentsService.getOptions(),
            ])
            .then(function ([employeeOptions, assessmentOptions]) {
                setEmployeeAssessmentOptions(assessmentOptions);
                setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                setEmployeeTypeOptions(employeeOptions.employeeTypes);

                setPageFilters([
                    {
                        name: "EMPLOYEESTATUS",
                        displayName: "Status",
                        items: employeeOptions.employeeStatuses.filter((status) => {
                            return status.active;
                        }),
                    },
                    {
                        name: "EMPLOYEETYPE",
                        displayName: "Type",
                        items: employeeOptions.employeeTypes,
                    },
                    {
                        name: "EMPLOYEECATEGORY",
                        displayName: "Category",
                        items: employeeOptions.employeeCategories,
                    },
                ]);
                setInitalSetupComplete(true);
            });
    }, []);

    return (
        <>
            <div className="spacer-bottom-small">
                <div className="filter-controls">
                    <div className="inline-items">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    inputRef={inputRef}
                                    placeholder="Search Employees"
                                    onChange={onSearchChange}
                                    value={searchTerm}
                                    large
                                    disabled={isLoading}
                                    icon="search"
                                    id="assessment-upcoming-search-field"
                                />
                            </div>
                        </div>
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={!initalSetupComplete}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListOpened={onLocationFilterOpened}
                            onLocationListClose={onLocationFilterClosed}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            disabled={isLoading}
                        />
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                    <Filter
                        filterName={"employeeAssessmentsUpcoming"}
                        visible={showFilters}
                        filters={pageFilters}
                        onUpdate={onFilterChange}
                        onClickOutside={onToggleFilter}
                    />
                </div>
            </div>
            <div className="spacer-bottom">
                <FormToggleList
                    disabled={isLoading}
                    items={filterStatuses}
                    value={selectedFilterStatus}
                    onChange={onFilterStatusChange}
                    className="restricted-width"
                />
            </div>
            <ListingTable
                showPerPage={false}
                pageSize={50}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
            />
        </>
    );
}
